import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {useColorScheme} from 'react-native';

type ThemePreference = 'light' | 'dark' | 'system';
type ThemeMode = 'light' | 'dark';

interface ThemeColors {
  // Header
  headerBackground: string;
  headerText: string;
  
  // Primary
  primary: string;
  primaryDark: string;
  primaryLight: string;
  primaryPastel: string;

  // Background
  background: string;
  surface: string;
  surfaceVariant: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textOnPrimary: string;

  // Border & Divider
  border: string;
  divider: string;

  // Status
  success: string;
  error: string;
  warning: string;
  info: string;
  
  // Cards
  cardBackground: string;
}

const lightTheme: ThemeColors = {
  // Light mode: Purple header with white/light content background
  headerBackground: '#441274',
  headerText: '#FFFFFF',
  
  primary: '#441274',
  primaryDark: '#330d57',
  primaryLight: '#6b3fa0',
  primaryPastel: '#E8DEFF',

  background: '#F5F5F5',
  surface: '#FFFFFF',
  surfaceVariant: '#F0F0F0',

  textPrimary: '#1A1A1A',
  textSecondary: '#666666',
  textOnPrimary: '#FFFFFF',

  border: '#E0E0E0',
  divider: '#EEEEEE',

  success: '#4CAF50',
  error: '#E53935',
  warning: '#FF9800',
  info: '#2196F3',
  
  cardBackground: '#FFFFFF',
};

const darkTheme: ThemeColors = {
  // Dark mode: Dark gray/near black background
  headerBackground: '#1A1A1A',
  headerText: '#FFFFFF',
  
  primary: '#A78BFA', // Brighter purple for visibility on dark (matches bottom tabs)
  primaryDark: '#8B5CF6',
  primaryLight: '#C4B5FD',
  primaryPastel: '#4C3A6D',

  background: '#0A0A0A',
  surface: '#1A1A1A',
  surfaceVariant: '#252525',

  textPrimary: '#FFFFFF',
  textSecondary: '#A0A0A0',
  textOnPrimary: '#FFFFFF',

  border: '#333333',
  divider: '#2A2A2A',

  success: '#4CAF50',
  error: '#EF5350',
  warning: '#FFA726',
  info: '#42A5F5',
  
  cardBackground: '#1A1A1A',
};

interface ThemeContextType {
  theme: ThemeMode;
  themePreference: ThemePreference;
  colors: ThemeColors;
  setThemePreference: (pref: ThemePreference) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = '@laviola_theme';

export const ThemeProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const systemColorScheme = useColorScheme();
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>('system');

  useEffect(() => {
    // Load saved theme preference
    AsyncStorage.getItem(THEME_STORAGE_KEY).then(savedPref => {
      if (savedPref === 'light' || savedPref === 'dark' || savedPref === 'system') {
        setThemePreferenceState(savedPref);
      }
    });
  }, []);

  const setThemePreference = (pref: ThemePreference) => {
    setThemePreferenceState(pref);
    AsyncStorage.setItem(THEME_STORAGE_KEY, pref);
  };

  // Resolve the actual theme mode
  const resolvedTheme: ThemeMode =
    themePreference === 'system'
      ? (systemColorScheme === 'dark' ? 'dark' : 'light')
      : themePreference;

  const colors = resolvedTheme === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{
      theme: resolvedTheme,
      themePreference,
      colors,
      setThemePreference,
      isDark: resolvedTheme === 'dark',
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Export static colors for backward compatibility
export const Colors = lightTheme;
